import { useEffect, useState } from "react";
import { deleteThread, fetchThreads, renameThread, type ThreadSummary } from "../../api/threads";

interface Props {
  bearer: string;
  collapsed: boolean;
  activeThreadId: number | null;
  refreshKey: number;
  onPick: (threadId: number) => void;
  onNew: () => void;
  onToggle: () => void;
}

export function Sidebar({
  bearer,
  collapsed,
  activeThreadId,
  refreshKey,
  onPick,
  onNew,
  onToggle,
}: Props) {
  const [threads, setThreads] = useState<ThreadSummary[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchThreads(bearer)
      .then((data) => {
        if (!cancelled) setThreads(data);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [bearer, refreshKey]);

  // Width transitions smoothly; inner content fades. We keep the aside
  // mounted in both states so the open/close feels continuous.
  return (
    <aside
      className={
        "shrink-0 border-r border-line bg-white h-[calc(100vh-70px)] sticky top-[70px] self-start " +
        "flex flex-col overflow-hidden transition-[width] duration-200 ease-out " +
        (collapsed ? "w-[56px]" : "w-[260px]")
      }
    >
      {/* Top action row: New + collapse toggle. Collapsed -> icon-only. */}
      <div
        className={
          "flex items-center gap-1.5 p-2 " + (collapsed ? "flex-col" : "")
        }
      >
        <button
          type="button"
          onClick={onToggle}
          className="h-9 w-9 inline-flex items-center justify-center rounded-lg text-neutral-500 hover:bg-chip hover:text-neutral-800 transition-colors"
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <PanelIcon collapsed={collapsed} />
        </button>
        <button
          type="button"
          onClick={onNew}
          className={
            "h-9 inline-flex items-center justify-center gap-2 rounded-lg text-neutral-700 hover:bg-chip hover:text-neutral-900 transition-colors " +
            (collapsed ? "w-9" : "flex-1 px-3 justify-start text-[13px] font-medium")
          }
          aria-label="New conversation"
          title="New conversation"
        >
          <PlusIcon />
          <span
            className={
              "transition-opacity duration-150 " + (collapsed ? "opacity-0 hidden" : "opacity-100")
            }
          >
            New chat
          </span>
        </button>
      </div>

      {/* Expanded: recent threads list. Collapsed: nothing here (the
          icon rail above is the only chrome that should show). */}
      <div
        className={
          "flex-1 overflow-y-auto overflow-x-hidden transition-opacity duration-150 " +
          (collapsed ? "opacity-0 pointer-events-none" : "opacity-100")
        }
      >
        <div className="px-3 pt-3 pb-1.5 text-[10px] uppercase tracking-[0.08em] text-neutral-400 font-semibold">
          Recent
        </div>
        <div className="px-1.5 space-y-px">
          {error && <div className="text-xs text-red-600 px-2">{error}</div>}
          {threads.length === 0 && !error && (
            <div className="text-[12px] text-neutral-400 px-2 py-2">No conversations yet.</div>
          )}
          {threads.map((t) => (
            <ThreadRow
              key={t.id}
              thread={t}
              active={activeThreadId === t.id}
              onPick={() => onPick(t.id)}
              onDelete={async () => {
                try {
                  await deleteThread(bearer, t.id);
                  setThreads((prev) => prev.filter((x) => x.id !== t.id));
                  if (activeThreadId === t.id) onNew();
                } catch (e) {
                  console.error("delete thread failed:", e);
                }
              }}
              onRename={async (title) => {
                try {
                  const saved = await renameThread(bearer, t.id, title);
                  setThreads((prev) => prev.map((x) => (x.id === t.id ? { ...x, title: saved } : x)));
                } catch (e) {
                  console.error("rename thread failed:", e);
                }
              }}
            />
          ))}
        </div>
      </div>
    </aside>
  );
}

/* ------------------------------------------------------------------ */
/* Icons                                                                */
/* ------------------------------------------------------------------ */

function PanelIcon({ collapsed }: { collapsed: boolean }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-[18px] w-[18px]">
      <rect x="3" y="4" width="18" height="16" rx="2.5" />
      <path d="M9 4v16" />
      {!collapsed && <path d="M14 9l-2 3 2 3" strokeLinecap="round" strokeLinejoin="round" />}
      {collapsed && <path d="M13 9l2 3-2 3" strokeLinecap="round" strokeLinejoin="round" />}
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg viewBox="0 0 16 16" className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 3v10M3 8h10" />
    </svg>
  );
}

interface ThreadRowProps {
  thread: ThreadSummary;
  active: boolean;
  onPick: () => void;
  onDelete: () => void;
  onRename: (title: string) => void;
}

function ThreadRow({ thread, active, onPick, onDelete, onRename }: ThreadRowProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(thread.title ?? "");
  const [confirming, setConfirming] = useState(false);

  if (editing) {
    return (
      <input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          if (draft.trim() && draft.trim() !== (thread.title ?? "")) onRename(draft.trim());
          setEditing(false);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            if (draft.trim() && draft.trim() !== (thread.title ?? "")) onRename(draft.trim());
            setEditing(false);
          } else if (e.key === "Escape") {
            setEditing(false);
            setDraft(thread.title ?? "");
          }
        }}
        className="w-full px-2 py-1.5 rounded-md text-[13px] border border-brand bg-white outline-none"
      />
    );
  }

  return (
    <div
      className={
        "group flex items-center gap-1 px-2 py-1 rounded-md transition-colors " +
        (active ? "bg-chip text-neutral-900" : "text-neutral-700 hover:bg-chip/70")
      }
    >
      <button
        type="button"
        onClick={onPick}
        onDoubleClick={() => {
          setDraft(thread.title ?? "");
          setEditing(true);
        }}
        className={
          "flex-1 min-w-0 text-left text-[13px] truncate " + (active ? "font-medium" : "")
        }
        title={thread.title ?? "Untitled"}
      >
        {thread.title || "Untitled"}
      </button>
      {confirming ? (
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={() => {
              onDelete();
              setConfirming(false);
            }}
            className="text-[11px] text-red-600 hover:underline"
          >
            Delete
          </button>
          <button
            type="button"
            onClick={() => setConfirming(false)}
            className="text-[11px] text-neutral-400 hover:text-neutral-700"
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          className="h-6 w-6 shrink-0 inline-flex items-center justify-center rounded text-neutral-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
          title="Delete thread"
        >
          <TrashIcon />
        </button>
      )}
    </div>
  );
}

function TrashIcon() {
  return (
    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path strokeLinecap="round" d="M3 4h10M6 4V3a1 1 0 011-1h2a1 1 0 011 1v1M5 4l1 9h4l1-9" />
    </svg>
  );
}
